/**
 * Custom analytics utility functions
 * This file contains helper functions for tracking events in Google Analytics
 */

// Track custom events
export const trackEvent = (category, action, label, value) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
    })
  }
}

// Track page views (for SPA navigation)
export const trackPageView = (url) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", "UA-162759252-1", {
      page_path: url,
    })

    // If you have GA4 configured
    window.gtag("config", "G-XXXXXXXXXX", {
      page_path: url,
    })
  }
}

// Track outbound links
export const trackOutboundLink = (url) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "click", {
      event_category: "outbound",
      event_label: url,
      transport_type: "beacon",
    })
  }
}

// Track file downloads
export const trackDownload = (fileUrl, fileType) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "download", {
      event_category: "file_download",
      event_label: fileUrl,
      file_type: fileType,
    })
  }
}

// Track form submissions
export const trackFormSubmission = (formName, formStatus) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "form_submit", {
      event_category: "forms",
      event_label: formName,
      form_status: formStatus,
    })
  }
}

// Track scroll depth
export const initScrollTracking = () => {
  if (typeof window !== "undefined") {
    const scrollDepths = [25, 50, 75, 90]
    const trackedDepths = []

    const calculateScrollDepth = () => {
      const scrollTop = window.pageYOffset
      const windowHeight = window.innerHeight
      const docHeight = document.documentElement.scrollHeight
      const totalScrollableDistance = docHeight - windowHeight
      const scrollPercentage = Math.floor(
        (scrollTop / totalScrollableDistance) * 100
      )

      scrollDepths.forEach((depth) => {
        if (scrollPercentage >= depth && !trackedDepths.includes(depth)) {
          trackedDepths.push(depth)
          trackEvent("scroll_depth", "scroll", `Scrolled ${depth}%`)
        }
      })
    }

    // Throttle to 250 ms so we don't saturate the main thread on every pixel scrolled.
    // passive: true lets the browser skip waiting for the handler before scrolling.
    let scrollThrottleTimer: ReturnType<typeof setTimeout> | null = null
    const throttledScrollDepth = () => {
      if (scrollThrottleTimer) return
      scrollThrottleTimer = setTimeout(() => {
        scrollThrottleTimer = null
        calculateScrollDepth()
      }, 250)
    }

    window.addEventListener("scroll", throttledScrollDepth, { passive: true })
  }
}

// Track time on page
export const initTimeOnPageTracking = () => {
  if (typeof window !== "undefined") {
    const timeIntervals = [30, 60, 120, 180, 300] // seconds
    const trackedIntervals = []
    const startTime = new Date()

    const checkTimeOnPage = () => {
      const currentTime = new Date()
      const timeSpent = Math.floor((currentTime - startTime) / 1000)

      timeIntervals.forEach((interval) => {
        if (timeSpent >= interval && !trackedIntervals.includes(interval)) {
          trackedIntervals.push(interval)
          trackEvent("time_on_page", "duration", `${interval} seconds`)
        }
      })
    }

    const intervalId = setInterval(checkTimeOnPage, 10000)

    // Clean up on page unload
    window.addEventListener("beforeunload", () => {
      clearInterval(intervalId)
    })
  }
}

// Initialize all tracking
export const initAnalytics = () => {
  if (typeof window !== "undefined") {
    initScrollTracking()
    initTimeOnPageTracking()
  }
}
